// @flow

import AppConfig from "shared/config/AppConfig"
import UserStoreBase from "@cabonline/shared/stores/UserStore"
import find from "lodash/find"
import type { AppType } from "shared/types/AppType"
import AppStore from "shared/stores/AppStore"
import MainApi from "shared/apis/MainApi"
import AuthStore from "shared/stores/AuthStore"
import Raven from "raven-js"

class UserStore extends UserStoreBase {
  constructor(
    appStore: typeof AppStore,
    mainApi: typeof MainApi,
    authStore: typeof AuthStore
  ) {
    super(appStore, mainApi, authStore)
  }

  _setRavenUserContext() {
    if (!AuthStore.auth) return

    const auth = AuthStore.auth

    Raven.setUserContext({
      email: auth.email || "unknown",
      id: auth.userId
    })
    Raven.setExtraContext({
      permissions: auth.permissions.slice(),
      tokenType: auth.tokenType,
      expiresAt: auth.expiresAt,
      expiresIn: auth.expiresIn
    })
  }

  async loadUserFromCookie(): Promise<boolean> {
    const status = await super.loadUserFromCookie()

    this._setRavenUserContext()

    return status
  }

  async login(email: string, password: string): Promise<boolean> {
    const success = await super.login(email, password)

    if (success) this._setRavenUserContext()

    return success
  }

  logout() {
    super.logout()
    Raven.setUserContext()
  }

  onlyHasStandaloneApp() {
    if (!this.authStore.auth) throw Error("No auth present")

    const internalApps = AppConfig.getInternalAppsForAuth(this.authStore.auth)

    return (
      internalApps.length === 1 && find(internalApps, app => app.standalone)
    )
  }

  getAvailableApps(): Array<AppType> {
    if (!this.authStore.auth) throw Error("No user present")

    return AppConfig.getInternalAppsForAuth(this.authStore.auth)
  }

  getAvailableAppByName(name: string): AppType {
    return find(this.getAvailableApps(), app => app.name === name)
  }
}

export default (new UserStore(AppStore, MainApi, AuthStore): any)
